import React from 'react';
import querystring from 'querystring';

import { Main, MainHeader, Body } from 'components/Main';
import { Panel, PanelTop } from 'components/Panel';
import { Table, Th, Td } from 'components/Table';
import PlayerLink from 'components/PlayerLink';

export default class PlayCricketMVPsPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { expanded: {} };
        this.toggleDetails = this.toggleDetails.bind(this);
        this.browserBack = this.browserBack.bind(this);
    }

    componentDidMount() {
        if (window !== undefined) window.addEventListener('popstate', this.browserBack);
    }

    componentWillUnmount() {
        if (window !== undefined) window.removeEventListener('popstate', this.browserBack);
    }

    browserBack() {
        let params = {};
        if (window.location.search) params = querystring.parse(window.location.search.substring(1));
        this.props.browserBack(window.location.pathname, params);
    }

    toggleDetails(pid) {
        this.setState({ expanded: { ...this.state.expanded, [pid]: !this.state.expanded[pid] } });
    }

    renderRows() {
        const rows = [];
        this.props.mvps.forEach((row, index) => {
            const expanded = Boolean(this.state.expanded[row.pid]);
            const name = `${row.fname} ${row.lname}`;
            rows.push(
                <tr key={`player-${row.pid}`} className={`cc-mvp-player-row cc-mvp-player-row-${index % 2 ? 'plain' : 'striped'}`}>
                    <Td min="8">{index + 1}</Td>
                    <Td left className="cc-mvp-player">
                        <PlayerLink pid={row.pid} fname={row.fname} lname={row.lname} />
                        <button
                            type="button"
                            className="cc-mvp-details-button"
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Hide' : 'Show'} MVP details for ${name}`}
                            onClick={() => this.toggleDetails(row.pid)}
                        >
                            More details <span aria-hidden="true">{expanded ? '\u25b2' : '\u25bc'}</span>
                        </button>
                    </Td>
                    <Td sortable>{row.matches}</Td>
                    <Td sortable>{row.batting}</Td>
                    <Td sortable>{row.bowling}</Td>
                    <Td sortable>{row.fielding}</Td>
                    <Td sortable>{row.team}</Td>
                    <Td sortable><strong>{row.overall}</strong></Td>
                    <Td sortable>{row.match_mvps}</Td>
                </tr>
            );
            if (expanded) {
                ['batting', 'bowling', 'fielding', 'team'].forEach(category => {
                    const details = row.breakdown[category];
                    rows.push(
                        <tr key={`${category}-${row.pid}`} className={`cc-mvp-detail-row cc-mvp-detail-row-${index % 2 ? 'plain' : 'striped'}`}>
                            <Td min="8" />
                            <Td left colspan="8">
                                <strong>{category.charAt(0).toUpperCase() + category.slice(1)}:</strong>{' '}
                                {details.length ? details.map((detail, detailIndex) => (
                                    <span key={detail.label}>
                                        {detailIndex ? ' \u00b7 ' : ''}{detail.label} {detail.points}
                                    </span>
                                )) : 'No points'}
                            </Td>
                        </tr>
                    );
                });
            }
        });
        return rows;
    }

    render() {
        const { sort, sortColumn } = this.props;
        return (
            <Main>
                <MainHeader />
                <Body id="play-cricket-mvps-page">
                    <Panel border>
                        <PanelTop title="Play Cricket MVPs" buttons>
                            <p className="cc-mvp-intro">Calculated using the Play-Cricket MVP rules. Select More details beside a player to see their breakdown. Boundary data may be unavailable for older matches.</p>
                        </PanelTop>
                        <Table striped hover className="cc-mvp-table">
                            <thead><tr>
                                <Th min="8" />
                                <Th left>Name</Th>
                                <Th sortable id="matches" sort={sort} onSort={sortColumn}>Matches</Th>
                                <Th sortable id="batting" sort={sort} onSort={sortColumn}>Batting</Th>
                                <Th sortable id="bowling" sort={sort} onSort={sortColumn}>Bowling</Th>
                                <Th sortable id="fielding" sort={sort} onSort={sortColumn}>Fielding</Th>
                                <Th sortable id="team" sort={sort} onSort={sortColumn}>Team</Th>
                                <Th sortable id="overall" sort={sort} onSort={sortColumn}>Overall</Th>
                                <Th sortable id="match_mvps" sort={sort} onSort={sortColumn}>Match MVPs</Th>
                            </tr></thead>
                            <tbody>{this.renderRows()}</tbody>
                        </Table>
                        <MVPRules />
                    </Panel>
                </Body>
            </Main>
        );
    }
}

const RuleGroup = ({ title, children }) => <section className="cc-mvp-rule-group"><h3>{title}</h3>{children}</section>;

const MVPRules = () => (
    <div className="cc-mvp-rules">
        <h2>Scoring rules</h2>
        <div className="cc-mvp-rules-grid">
            <RuleGroup title="Batting"><ul>
                <li>1 point for every 20 runs in an innings</li><li>1 point for reaching 50</li>
                <li>1 additional point for reaching 100</li><li>1 point for scoring more than 20% of the team's runs</li>
                <li>1 point for hitting at least eight boundaries (only available for newer matches where boundary data was recorded)</li>
            </ul></RuleGroup>
            <RuleGroup title="Bowling"><ul>
                <li>1 point per wicket</li><li>1 point for taking at least three wickets in an innings</li>
                <li>1 additional point for taking at least five wickets in an innings</li><li>1 point for bowling at least two maidens</li>
                <li>1 point for an economy rate of 3.0 or lower</li>
            </ul></RuleGroup>
            <RuleGroup title="Fielding"><ul>
                <li>1 point per catch</li><li>1 point per run-out</li><li>1 point per stumping</li>
                <li>1 point for at least three fielding dismissals in an innings</li>
            </ul></RuleGroup>
            <RuleGroup title="Team"><ul><li>1 point for each member of a winning team</li></ul>
                <p className="cc-mvp-note">Play-Cricket also awards 1 point to the winning captain; this is not included because captains are not recorded.</p>
            </RuleGroup>
        </div>
        <p className="cc-mvp-match-awards">A player earns a Match MVP when they have the highest overall points in a completed match. Tied leaders each earn one. No Match MVP is awarded when the highest score is zero or the result is not a Win, Loss, Draw or Tie.</p>
    </div>
);



// WEBPACK FOOTER //
// ./src/views/play-cricket-mvps/PlayCricketMVPsPage.jsx